package org.teddapp.project.ui

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.scale
import androidx.compose.ui.graphics.vector.PathParser
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Icônes TEDD : le jeu du design system, complété par les icônes propres à l'application.
 * Tous les chemins sont en viewBox 24×24, au trait seul, extrémités rondes. Une icône par
 * module : `today`, `focus`, `portfolio`, `goals`, `notes`, `habits`, `finance`, `inbox`.
 */
private val TeddIconPaths: Map<String, String> = mapOf(
    // ── Jeu du design system : grille 24, trait 1.75, extrémités rondes ──
    "check" to "M5 12.5l4.5 4.5L19 7.5",
    "plus" to "M12 5v14M5 12h14",
    "x" to "M6 6l12 12M18 6L6 18",
    "search" to "M4.5 11a6.5 6.5 0 1 0 13 0a6.5 6.5 0 1 0 -13 0M16 16l4 4",
    "today" to "M8 12a4 4 0 1 0 8 0a4 4 0 1 0 -8 0M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4",
    "focus" to "M4.5 13.5a7.5 7.5 0 1 0 15 0a7.5 7.5 0 1 0 -15 0M12 13.5V10M9.5 2.5h5M18.5 6l1.5-1.5",
    "portfolio" to "M6.5 9a5.5 5.5 0 1 0 11 0a5.5 5.5 0 1 0 -11 0M8.6 13.4L7 21l5-2.6 5 2.6-1.6-7.6",
    "goals" to "M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M7 12a5 5 0 1 0 10 0a5 5 0 1 0 -10 0M10.5 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0",
    "notes" to "M6 3.5h11a1.5 1.5 0 0 1 1.5 1.5v14a1.5 1.5 0 0 1-1.5 1.5H6zM9.5 3.5v17M12.5 8h3M12.5 11.5h3",
    "habits" to "M12 21c-3.6 0-6-2.4-6-5.6 0-3.4 2.6-5 3.6-8.9 1.6 1.2 2.4 2.8 2.4 4.9 1-.6 1.6-1.6 1.8-2.8 2 1.6 4.2 3.8 4.2 6.8 0 3.2-2.4 5.6-6 5.6z",
    "finance" to "M3.5 8.5a2.5 2.5 0 0 1 2.5-2.5h12a2.5 2.5 0 0 1 2.5 2.5v9a2.5 2.5 0 0 1-2.5 2.5H6a2.5 2.5 0 0 1-2.5-2.5zM3.5 10h17M15.5 14.5h2",
    "inbox" to "M5 5h14a1.5 1.5 0 0 1 1.5 1.5v8.5a1.5 1.5 0 0 1-1.5 1.5h-8.5l-4.5 3.5v-3.5H5a1.5 1.5 0 0 1-1.5-1.5V6.5A1.5 1.5 0 0 1 5 5z",
    "arrow-right" to "M5 12h14M13 6l6 6-6 6",
    "chevron-right" to "M9.5 6l6 6-6 6",
    "chevron-down" to "M6 9.5l6 6 6-6",
    "globe" to "M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M3.5 12h17M12 3.5c2.4 2.6 3.5 5.4 3.5 8.5s-1.1 5.9-3.5 8.5c-2.4-2.6-3.5-5.4-3.5-8.5s1.1-5.9 3.5-8.5z",
    "lock" to "M7.5 10.5h9a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2zM8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5",
    "calendar" to "M6 5h12a2.5 2.5 0 0 1 2.5 2.5v10.5a2.5 2.5 0 0 1-2.5 2.5H6a2.5 2.5 0 0 1-2.5-2.5V7.5A2.5 2.5 0 0 1 6 5zM3.5 10h17M8 3v4M16 3v4",
    "repeat" to "M17 3.5l3 3-3 3M20 6.5H8a4 4 0 0 0-4 4v1M7 20.5l-3-3 3-3M4 17.5h12a4 4 0 0 0 4-4v-1",
    "play" to "M8 5.5v13l10.5-6.5z",
    "pause" to "M9 5.5v13M15 5.5v13",
    "more" to "M6 12h.01M12 12h.01M18 12h.01",
    "send" to "M5 12h13M12 5.5l6.5 6.5-6.5 6.5",
    "flag" to "M5.5 21V4M5.5 4.5h11l-2 4 2 4h-11",
    "cards" to "M12 3.5l8.5 4.5-8.5 4.5L3.5 8zM3.5 12.5l8.5 4.5 8.5-4.5M3.5 16.5l8.5 4.5 8.5-4.5",
    "trend" to "M3.5 16.5l6-6 4 4 7-7M15 7.5h5.5V13",
    "trash" to "M4.5 6.5h15M9.5 6.5V4h5v2.5M6.5 6.5l1 13.5h9l1-13.5",
    "edit" to "M14.5 5l4.5 4.5L9 19.5H4.5V15z",
    "clock" to "M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0M12 7.5V12l3 2",
    "alert" to "M12 3.5l9 16H3zM12 10v4M12 17h.01",
    "command" to "M9 9h6v6H9zM9 9V6.5A2.5 2.5 0 1 0 6.5 9H9M15 9h2.5A2.5 2.5 0 1 0 15 6.5V9M15 15v2.5a2.5 2.5 0 1 0 2.5-2.5H15M9 15H6.5A2.5 2.5 0 1 0 9 17.5V15",
    "enter" to "M19 5v7.5a2 2 0 0 1-2 2H5M9 10.5l-4 4 4 4",
    "eye" to "M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12zM9.5 12a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0",
    // ── Icônes propres à l'application, même grille et même trait ──
    "target"   to "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-4a6 6 0 1 0 0-12 6 6 0 0 0 0 12zm0-4a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
    "heart"    to "M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z",
    "wallet"   to "M3 7a2 2 0 0 1 2-2h14v2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m0 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2z",
    "book"     to "M4 4h12a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4zm3 0v13h12",
    "spark"    to "M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2zm6 11l.8 2.2L21 16l-2.2.8L18 19l-.8-2.2L15 16l2.2-.8L18 13z",
    "users"    to "M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm8 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 21v-1a6 6 0 0 1 12 0v1H2zm13 0v-1a6 6 0 0 0-2.3-4.7A5 5 0 0 1 22 20v1h-7z",
    "home"     to "M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-6h-6v6H5a2 2 0 0 1-2-2v-9z",
    "list"     to "M4 6h16M4 12h16M4 18h10",
    "grid"     to "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
    "chevron"  to "M9 6l6 6-6 6",
    "flame"    to "M12 22a6 6 0 0 0 6-6c0-3-2-5-4-9-1 4-4 4-4 8a2 2 0 1 0 4 0c0-1.5-.5-2-2-3M12 22a6 6 0 0 1-6-6c0-3 1-4 3-7",
    "settings" to "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm9 4l-1.5 1 .5 2-2 .5-.5 2-2-.5L14 19l-2-1-2 1-1.5-2-2 .5-.5-2-2-.5.5-2L3 12l1.5-1-.5-2 2-.5.5-2 2 .5L10 5l2 1 2-1 1.5 2 2-.5.5 2 2 .5-.5 2L21 12z",
    "note"     to "M4 5h10l4 4v10H4V5zm10 0v4h4",
    "eye-off"  to "M3 3l18 18M2 12s4-7 10-7c2 0 4 .6 5.5 1.7M22 12s-4 7-10 7c-2 0-3.7-.6-5-1.5M9.5 9.5a3 3 0 0 0 4 4",
    "arrow"    to "M5 12h14M13 6l6 6-6 6",
    "pin"      to "M12 17v5M5 9l7-7 7 7-3 3 3 3H8l3-3-6-3z",
    "category" to "M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z",
    "arrow-up" to "M12 19V5M5 12l7-7 7 7",
    "arrow-down" to "M12 5v14M19 12l-7 7-7-7",
    "print"    to "M6 9V3h12v6M6 18h12v3H6zM4 9h16v9H4z",
    "bell"     to "M6 8a6 6 0 0 1 12 0v5l2 2v1H4v-1l2-2V8zm5 12a2 2 0 0 0 4 0",
    "chevron-left" to "M15 18l-6-6 6-6",
    // 2×3 dots, style grip handle pour drag&drop
    "grip"     to "M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01",
    "idcard"   to "M3 6h18v12H3zM6.5 14.5c.6-1.6 4.4-1.6 5 0M9 11.2a1.4 1.4 0 1 0 0-2.8a1.4 1.4 0 0 0 0 2.8M14.5 10H18M14.5 13.5H18",
    "mindmap"  to "M9.5 12h5M14.5 12c2 0 2-5 4-5M14.5 12c2 0 2 5 4 5M9.5 12c-2 0-2-5-4-5M9.5 12c-2 0-2 5-4 5M12 12a2.5 2.5 0 1 0 0-5a2.5 2.5 0 0 0 0 5M18.5 7h.01M18.5 17h.01M5.5 7h.01M5.5 17h.01",
    "minus"    to "M5 12h14",
    "zoom-in"  to "M4.5 11a6.5 6.5 0 1 0 13 0a6.5 6.5 0 1 0-13 0M15.6 15.6L20 20M11 8.5v5M8.5 11h5",
    "zoom-out" to "M4.5 11a6.5 6.5 0 1 0 13 0a6.5 6.5 0 1 0-13 0M15.6 15.6L20 20M8.5 11h5",
    "fit"      to "M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5",
    "fold"     to "M6 4.5h12M6 19.5h12M12 8v8M9 13l3 3 3-3",
    "unfold"   to "M6 4.5h12M6 19.5h12M12 9v6M9 11l3-3 3 3M9 13l3 3 3-3",
    "image"    to "M4 5.5h16v13H4zM4 15l4.5-4.5 4 4 2.5-2.5L20 17M15.5 9.5h.01",
    "refresh"  to "M20 11a8 8 0 0 0-14.5-4.5L4 8M4 4v4h4M4 13a8 8 0 0 0 14.5 4.5L20 16M20 20v-4h-4",
    // Flèche qui revient en arrière : restaurer, annuler
    "undo"     to "M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11",
    "star"     to "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z",
    "highlight" to "M14.5 4.5l5 5-8 8H6.5v-5zM4 20.5h16",
    "sort"     to "M7 4.5v15M4 16.5l3 3 3-3M13.5 6.5h7M13.5 12h5M13.5 17.5h3",
    "eraser"   to "M8.5 20.5h11M4.8 15.2l9.9-9.9a2 2 0 0 1 2.8 0l2.2 2.2a2 2 0 0 1 0 2.8l-8.6 8.6a3 3 0 0 1-2.1.9H8.4a3 3 0 0 1-2.1-.9l-1.5-1.5a1.5 1.5 0 0 1 0-2.2zM9.5 10.5l5 5",
    "draw"     to "M4 20l1.2-4.6L15.6 5a2.1 2.1 0 0 1 3 0l.4.4a2.1 2.1 0 0 1 0 3L8.6 18.8zM13.5 7l3.5 3.5M4 20c3-1 5.5-.5 8 0s5 .5 8-1",
    "redo"     to "M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13",
    // Mise en forme des notes
    "list-bullet"  to "M9.5 6h10.5M9.5 12h10.5M9.5 18h10.5M5 6h.01M5 12h.01M5 18h.01",
    "list-ordered" to "M10 6h10M10 12h10M10 18h10M4 4.5h1.5V9M4 9h3M4 14.5c.4-1 2.8-1 2.8.4 0 1.2-2.8 2.1-2.8 3.6h3",
    "task"     to "M4 4.5h5.5V10H4zM4 14h5.5v5.5H4zM5.4 16.8l1.2 1.2 2-2.5M13 7.25h7M13 16.75h7",
    "quote"    to "M5 7h4.5v4.5c0 3-1.5 5-4 5.5M14.5 7H19v4.5c0 3-1.5 5-4 5.5",
    "code"     to "M8.5 7.5L4 12l4.5 4.5M15.5 7.5L20 12l-4.5 4.5M13.5 5l-3 14",
    "arrow-left" to "M19 12H5M11 6l-6 6 6 6",
    "layers"   to "M12 3.5l8.5 4.5L12 12.5 3.5 8zM3.5 12l8.5 4.5 8.5-4.5M3.5 16l8.5 4.5 8.5-4.5",
    "mail"     to "M3.5 6h17v12h-17zM3.5 7l8.5 6.5L20.5 7",
    "phone"    to "M8 3.5h8a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1zM11 17.5h2",
    "link"     to "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1",
    "share"    to "M12 15V4M8 8l4-4 4 4M5 12v7h14v-7",
    "copy"     to "M9 9h10v11H9zM5 15V4h10",
)

@Composable
fun TeddIcon(
    name: String,
    modifier: Modifier = Modifier,
    size: Dp = 16.dp,
    color: Color = TeddTokens.Ink,
    strokeWidth: Dp = 1.75.dp,
) {
    val pathString = TeddIconPaths[name] ?: TeddIconPaths["target"]!!
    val path: Path = remember(name) {
        PathParser().parsePathString(pathString).toPath()
    }
    Canvas(modifier = modifier.size(size)) {
        val canvasSize = this.size.minDimension
        val scaleFactor = canvasSize / 24f
        val strokePx = strokeWidth.toPx()
        scale(scale = scaleFactor, pivot = Offset.Zero) {
            drawPath(
                path = path,
                color = color,
                style = Stroke(
                    width = strokePx / scaleFactor,
                    cap = StrokeCap.Round,
                    join = StrokeJoin.Round,
                ),
            )
        }
    }
}
